import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, ArrowLeft, Key, Copy, Check, Plus, Trash2, Shield, Globe,
  Code, BookOpen, Send, Webhook, RefreshCw, ChevronDown, ChevronRight,
  Terminal, Cpu, Radio, Target, ExternalLink, Eye, EyeOff, Settings,
  AlertTriangle, Clock, Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Copyable code block
function CodeBlock({ code, language = 'bash', label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-[#0D1017] border border-white/10 overflow-hidden" data-testid={`code-block-${label}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#11141D] border-b border-white/5">
        <span className="text-[10px] font-mono text-slate-500">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-white transition-colors">
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <pre className="p-3 text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre leading-relaxed">
        {code}
      </pre>
    </div>
  );
}

// Collapsible section
function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#090B0F] border border-white/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-[#11141D] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-emerald-500" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{title}</h3>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="p-4 pt-0 space-y-4">{children}</div>}
    </div>
  );
}

// Endpoint documentation card
function EndpointDoc({ method, path, description, params, response, requiresAuth = true }) {
  const methodColors = {
    GET: 'text-emerald-400 bg-emerald-400/10',
    POST: 'text-blue-400 bg-blue-400/10',
    PUT: 'text-amber-400 bg-amber-400/10',
    DELETE: 'text-red-400 bg-red-400/10',
  };
  return (
    <div className="bg-[#11141D] border border-white/5 p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-mono font-bold px-2 py-0.5 ${methodColors[method]}`}>{method}</span>
        <span className="font-mono text-sm text-white">{path}</span>
        {requiresAuth && <Key className="w-3 h-3 text-amber-400" />}
      </div>
      <p className="text-xs text-slate-400">{description}</p>
      {params && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('developer.parameters')}</p>
          <div className="space-y-1">
            {params.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-purple-400">{p.name}</span>
                <span className="text-slate-600">({p.type})</span>
                <span className="text-slate-400">{p.desc}</span>
                {p.required && <span className="text-red-400 text-[10px]">*</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {response && (
        <CodeBlock code={response} language="json" label={`response-${path}`} />
      )}
    </div>
  );
}

// API Key Manager
function ApiKeyManager() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ platform_name: '', description: '', subscribed_agents: '' });
  const [agents, setAgents] = useState([]);

  const fetchKeys = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/api-keys`, { withCredentials: true });
      setKeys(data.api_keys || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/agents`, { withCredentials: true });
      setAgents(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchKeys(); fetchAgents(); }, [fetchKeys, fetchAgents]);

  const createKey = async () => {
    try {
      const agentList = form.subscribed_agents
        ? form.subscribed_agents.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/api-keys`, {
        platform_name: form.platform_name,
        description: form.description,
        subscribed_agents: agentList,
        permissions: { signals: true, execute: true, webhooks: true },
      }, { withCredentials: true });
      setNewKey(data);
      setShowCreate(false);
      setForm({ platform_name: '', description: '', subscribed_agents: '' });
      fetchKeys();
    } catch (err) { console.error(err); }
  };

  const revokeKey = async (keyPrefix) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/api-keys/${keyPrefix}`, { withCredentials: true });
      fetchKeys();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-4" data-testid="api-key-manager">
      <div className="flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-wider text-slate-500">{t('developer.registeredKeys')}</h4>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs font-mono hover:bg-emerald-500/30 transition-colors"
          data-testid="create-api-key-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva Key
        </button>
      </div>

      {/* New key created banner */}
      {newKey && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-4" data-testid="new-key-banner">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400 font-bold">Key creada para {newKey.platform_name}</span>
          </div>
          <p className="text-[10px] text-amber-400 mb-2">{t('developer.copyKeyNow')}</p>
          <div className="flex items-center gap-2">
            <code className="bg-[#0D1017] px-3 py-1.5 font-mono text-xs text-white flex-1 select-all">{newKey.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey.key); }} className="px-2 py-1.5 bg-emerald-500/20 text-emerald-400">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-xs text-slate-500 mt-2 hover:text-white">{t('common.close')}</button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="bg-[#11141D] border border-white/10 p-4 space-y-3" data-testid="create-key-form">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t('developer.platformName')}</label>
            <input
              value={form.platform_name} onChange={(e) => setForm({ ...form, platform_name: e.target.value })}
              placeholder="IntangiTrade"
              className="w-full bg-[#0D1017] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
              data-testid="key-platform-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t('developer.description')}</label>
            <input
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="API key for production trading"
              className="w-full bg-[#0D1017] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
              data-testid="key-description-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{t('developer.subscribedAgents')}</label>
            <input
              value={form.subscribed_agents} onChange={(e) => setForm({ ...form, subscribed_agents: e.target.value })}
              placeholder="AGT-01, AGT-07, AGT-15"
              className="w-full bg-[#0D1017] border border-white/10 text-white px-3 py-2 font-mono text-sm focus:border-emerald-500/50 outline-none"
              data-testid="key-agents-input"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {agents.slice(0, 17).map(a => (
              <button key={a.id} onClick={() => {
                const current = form.subscribed_agents ? form.subscribed_agents.split(',').map(s => s.trim()) : [];
                if (current.includes(a.id)) {
                  setForm({ ...form, subscribed_agents: current.filter(x => x !== a.id).join(', ') });
                } else {
                  setForm({ ...form, subscribed_agents: [...current, a.id].filter(Boolean).join(', ') });
                }
              }}
                className={`text-[10px] font-mono px-2 py-1 border transition-colors ${form.subscribed_agents.includes(a.id) ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-white/10 text-slate-500 hover:text-white'}`}
              >{a.id}</button>
            ))}
          </div>
          <button onClick={createKey} disabled={!form.platform_name}
            className="w-full bg-emerald-500 text-black py-2 font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-30"
            data-testid="submit-create-key-btn"
          >{t('developer.generateKey')}</button>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 text-slate-500 animate-spin" /></div>
      ) : keys.length === 0 ? (
        <div className="text-center py-6">
          <Key className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-xs font-mono">{t('developer.noKeys')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.key_masked} className="bg-[#11141D] border border-white/5 p-3 flex items-center justify-between" data-testid={`api-key-row-${k.platform_name}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-white text-sm">{k.platform_name}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 ${k.active ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>
                    {k.active ? 'ACTIVE' : 'REVOKED'}
                  </span>
                </div>
                <p className="font-mono text-xs text-slate-500">{k.key_masked}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                  <span>Agents: {k.subscribed_agents?.length ? k.subscribed_agents.join(', ') : 'All'}</span>
                  <span>Requests: {k.requests_count || 0}</span>
                  {k.last_used && <span>Last used: {new Date(k.last_used).toLocaleDateString()}</span>}
                </div>
              </div>
              {k.active && (
                <button onClick={() => revokeKey(k.key_masked?.slice(0, 12))} className="text-red-400 hover:text-red-300 p-2" data-testid={`revoke-key-${k.platform_name}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Main Developer Portal
export default function DeveloperPortal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const baseUrl = BACKEND_URL;

  return (
    <div className="min-h-screen bg-[#030508]" data-testid="developer-portal">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030508]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#030508]" />
              </div>
              <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
                BlackOre<span className="text-emerald-500">X</span>
              </span>
              <span className="text-xs font-mono text-slate-500 ml-2">{t('developer.title')}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href={`${baseUrl}/docs`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 text-xs font-mono hover:bg-blue-500/30 transition-colors"
              data-testid="swagger-link"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Swagger
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Hero */}
        <div className="bg-[#090B0F] border border-white/10 p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-black text-white mb-2">
            Agents-as-a-Service <span className="text-emerald-500">API</span>
          </h1>
          <p className="text-slate-400 text-sm max-w-2xl mb-4">
            Conecta tu plataforma a los 17 agentes IA de BlackOre X. Recibe senales en tiempo real,
            registra webhooks para push automatico, o delega la ejecucion completa a nuestros agentes.
          </p>
          <div className="flex flex-wrap gap-4 text-xs font-mono">
            <div className="flex items-center gap-1 text-emerald-400">
              <Radio className="w-3.5 h-3.5" /> Signals API
            </div>
            <div className="flex items-center gap-1 text-blue-400">
              <Webhook className="w-3.5 h-3.5" /> Webhooks (Push)
            </div>
            <div className="flex items-center gap-1 text-purple-400">
              <Send className="w-3.5 h-3.5" /> Delegated Execution
            </div>
            <div className="flex items-center gap-1 text-amber-400">
              <Shield className="w-3.5 h-3.5" /> API Key Auth
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <Section title="Quick Start" icon={Terminal} defaultOpen={true}>
          <p className="text-xs text-slate-400 mb-3">
            1. Genera una API Key desde el panel de abajo. 2. Usa el header <code className="text-emerald-400">X-API-Key</code> en cada request.
          </p>
          <CodeBlock label="quickstart-curl" language="bash" code={`# 1. Obtener senales de todos tus agentes suscritos
curl -s "${baseUrl}/api/v1/signals" \\
  -H "X-API-Key: \${YOUR_API_KEY}" | python3 -m json.tool

# 2. Obtener senales de un agente especifico
curl -s "${baseUrl}/api/v1/signals/AGT-07" \\
  -H "X-API-Key: \${YOUR_API_KEY}"

# 3. Register a webhook to receive push signals
curl -X POST "${baseUrl}/api/v1/webhooks" \\
  -H "X-API-Key: \${YOUR_API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://tu-plataforma.com/webhook", "events": ["signal", "trade"]}'

# 4. Execute a delegated order via AGT-15
curl -X POST "${baseUrl}/api/v1/execute" \\
  -H "X-API-Key: \${YOUR_API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agent_id": "AGT-15",
    "symbol": "IPIX/USD",
    "side": "buy",
    "order_type": "bracket",
    "quantity": 100,
    "price": 0.75,
    "stop_loss": 0.71,
    "take_profit": 0.82
  }'`} />

          <CodeBlock label="quickstart-python" language="python" code={`import requests

API_KEY = "YOUR_API_KEY"  # Replace with your generated key
BASE = "${baseUrl}/api/v1"
HEADERS = {"X-API-Key": API_KEY}

# Obtener catalogo de agentes
agents = requests.get(f"{BASE}/agents", headers=HEADERS).json()
for a in agents["agents"]:
    print(f"{a['agent_id']} | {a['name']} | Return: {a['performance']['total_return_pct']}%")

# Stream de senales
signals = requests.get(f"{BASE}/signals?limit=10", headers=HEADERS).json()
for s in signals["signals"]:
    print(f"[{s['agent_id']}] {s['side']} {s['symbol']} @ {s['price']} conf={s['confidence']}")

# Ejecucion delegada (bracket order via AGT-15)
order = requests.post(f"{BASE}/execute", headers=HEADERS, json={
    "agent_id": "AGT-15",
    "symbol": "IPIX/USD",
    "side": "buy",
    "order_type": "bracket",
    "quantity": 100,
    "price": 0.75,
    "stop_loss": 0.71,
    "take_profit": 0.82,
}).json()
print(f"Order {order['order_id']}: {order['status']}")`} />
        </Section>

        {/* API Key Management */}
        <Section title="API Keys" icon={Key} defaultOpen={true}>
          <ApiKeyManager />
        </Section>

        {/* Signals API Docs */}
        <Section title="Signals API" icon={Radio}>
          <EndpointDoc method="GET" path="/api/v1/signals" description="Obtener senales recientes de agentes suscritos."
            params={[
              { name: 'agent_id', type: 'string', desc: 'Filtrar por agente', required: false },
              { name: 'limit', type: 'int', desc: 'Max resultados (default 50, max 200)', required: false },
            ]}
            response={`{
  "signals": [
    {
      "signal_id": "SIG-A1B2C3",
      "agent_id": "AGT-07",
      "symbol": "BTCUSDT",
      "side": "BUY",
      "price": 97500.0,
      "confidence": 0.85,
      "stop_loss": 95000.0,
      "take_profit": 102000.0,
      "strategy": "momentum_breakout",
      "timestamp": "2026-03-30T12:00:00Z"
    }
  ],
  "count": 1
}`} />
          <EndpointDoc method="GET" path="/api/v1/signals/{agent_id}" description="Obtener senales de un agente especifico."
            params={[{ name: 'limit', type: 'int', desc: 'Max resultados', required: false }]}
          />
          <EndpointDoc method="GET" path="/api/v1/agents" description="Listar todos los agentes disponibles con metricas de performance." />
          <EndpointDoc method="GET" path="/api/v1/agents/{agent_id}" description="Full detail of a specific agent." />
          <EndpointDoc method="GET" path="/api/v1/status" description="Estado del engine y resumen de performance general." />
        </Section>

        {/* Webhooks Docs */}
        <Section title="Webhooks (Real-time Push)" icon={Webhook}>
          <p className="text-xs text-slate-400 mb-3">
            Register a URL to receive push notifications every time an agent generates a signal, executes a trade, or completes an engine cycle.
          </p>
          <EndpointDoc method="POST" path="/api/v1/webhooks" description="Register a webhook."
            params={[
              { name: 'url', type: 'string', desc: 'URL HTTPS que recibira los eventos', required: true },
              { name: 'events', type: 'array', desc: '"signal", "trade", "cycle"', required: false },
              { name: 'agent_ids', type: 'array', desc: 'Filtrar por agentes (vacio = todos suscritos)', required: false },
            ]}
            response={`{
  "webhook_id": "wh_a1b2c3d4e5f6g7h8",
  "url": "https://tu-plataforma.com/webhook",
  "events": ["signal", "trade"],
  "agent_ids": ["AGT-07", "AGT-15"],
  "active": true
}`} />
          <EndpointDoc method="GET" path="/api/v1/webhooks" description="Listar webhooks registrados." />
          <EndpointDoc method="PUT" path="/api/v1/webhooks/{webhook_id}" description="Update webhook configuration." />
          <EndpointDoc method="DELETE" path="/api/v1/webhooks/{webhook_id}" description="Eliminar un webhook." />

          <div className="bg-[#11141D] border border-white/5 p-4">
            <p className="text-xs text-slate-400 mb-2">{t('developer.payloadFormat')}</p>
            <CodeBlock label="webhook-payload" language="json" code={`{
  "event": "signal",
  "webhook_id": "wh_a1b2c3d4e5f6g7h8",
  "timestamp": "2026-03-30T12:00:00Z",
  "data": {
    "agent_id": "AGT-07",
    "symbol": "BTCUSDT",
    "side": "BUY",
    "price": 97500.0,
    "confidence": 0.85,
    "stop_loss": 95000.0,
    "take_profit": 102000.0,
    "strategy": "momentum_breakout"
  }
}`} />
          </div>
        </Section>

        {/* Delegated Execution Docs */}
        <Section title="Delegated Execution" icon={Send}>
          <p className="text-xs text-slate-400 mb-3">
            Envia ordenes que seran ejecutadas a traves de la logica de riesgo del agente seleccionado.
            AGT-15 ejecuta ordenes reales en IntangiHub. Otros agentes las procesan como paper trades.
          </p>
          <EndpointDoc method="POST" path="/api/v1/execute" description="Enviar una orden para ejecucion delegada."
            params={[
              { name: 'agent_id', type: 'string', desc: 'Agent that will execute (e.g.: AGT-15)', required: true },
              { name: 'symbol', type: 'string', desc: 'Simbolo del activo (ej: IPIX/USD)', required: true },
              { name: 'side', type: 'string', desc: '"buy" o "sell"', required: true },
              { name: 'order_type', type: 'string', desc: '"market", "limit", "bracket"', required: false },
              { name: 'quantity', type: 'float', desc: 'Cantidad a operar', required: false },
              { name: 'price', type: 'float', desc: 'Precio (para limit/bracket)', required: false },
              { name: 'stop_loss', type: 'float', desc: 'Precio de stop loss', required: false },
              { name: 'take_profit', type: 'float', desc: 'Precio de take profit', required: false },
            ]}
            response={`{
  "order_id": "ext_a1b2c3d4e5f6g7h8",
  "agent_id": "AGT-15",
  "symbol": "IPIX/USD",
  "side": "buy",
  "order_type": "bracket",
  "status": "executed",
  "result": {
    "bracket_group_id": "BRK-XXXXXXXX",
    "entry": {"price": 0.75, "status": "open"},
    "stop_loss": {"price": 0.71, "status": "open"},
    "take_profit": {"price": 0.82, "status": "open"}
  }
}`} />
          <EndpointDoc method="GET" path="/api/v1/orders" description="Listar ordenes enviadas."
            params={[
              { name: 'status', type: 'string', desc: '"pending", "executed", "failed", "queued"', required: false },
              { name: 'limit', type: 'int', desc: 'Max resultados', required: false },
            ]}
          />
          <EndpointDoc method="GET" path="/api/v1/orders/{order_id}" description="Detail of a specific order." />
        </Section>

        {/* Integration Example */}
        <Section title="Ejemplo: Integracion con IntangiTrade" icon={Globe}>
          <CodeBlock label="intangitrade-example" language="python" code={`"""
IntangiTrade Integration Example
Consume BlackOre X agents to automate IP asset trading
"""
import requests
import time

API_KEY = "YOUR_API_KEY"  # Replace with your generated key
BASE = "${baseUrl}/api/v1"
H = {"X-API-Key": API_KEY, "Content-Type": "application/json"}

# 1. Ver agentes disponibles y su performance
agents = requests.get(f"{BASE}/agents", headers=H).json()
for a in agents["agents"]:
    if a["subscribed"]:
        perf = a["performance"]
        print(f"  {a['agent_id']} {a['name']}: Return={perf['total_return_pct']}% Sharpe={perf['sharpe_ratio']}")

# 2. Polling de senales cada 30 segundos
print("\\nEscuchando senales...")
while True:
    signals = requests.get(f"{BASE}/signals?limit=5", headers=H).json()
    for s in signals["signals"]:
        print(f"  [{s['agent_id']}] {s['side']} {s['symbol']} @ {s['price']}")
        
        # 3. Auto-ejecutar bracket orders en IP assets
        if s["symbol"].startswith("IPIX") or s["symbol"].startswith("PATENT"):
            order = requests.post(f"{BASE}/execute", headers=H, json={
                "agent_id": s["agent_id"],
                "symbol": s["symbol"],
                "side": s["side"].lower(),
                "order_type": "bracket",
                "quantity": 100,
                "price": s["price"],
                "stop_loss": s.get("stop_loss", s["price"] * 0.95),
                "take_profit": s.get("take_profit", s["price"] * 1.10),
            }).json()
            print(f"    -> Orden {order['order_id']}: {order['status']}")
    
    time.sleep(30)

# --- O usar webhooks para zero-latency ---
# requests.post(f"{BASE}/webhooks", headers=H, json={
#     "url": "https://intangitrade.com/api/blackore-webhook",
#     "events": ["signal"],
#     "agent_ids": ["AGT-15"]
# })`} />
        </Section>

        {/* Rate Limits */}
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('developer.rateLimits')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="bg-[#11141D] p-3">
              <p className="text-slate-500 mb-1">{t('developer.defaultRateLimit')}</p>
              <p className="font-mono text-white">1,000 req/hora</p>
            </div>
            <div className="bg-[#11141D] p-3">
              <p className="text-slate-500 mb-1">{t('developer.authentication')}</p>
              <p className="font-mono text-white">{t('developer.headerApiKey')}</p>
            </div>
            <div className="bg-[#11141D] p-3">
              <p className="text-slate-500 mb-1">{t('developer.engineCycle')}</p>
              <p className="font-mono text-white">{t('developer.cycleTime')}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
